import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { networkManager, getErrorMessage } from '../api/NetworkManager';
import {
  ArrowLeft,
  Loader2,
  Play,
  Pause,
  SkipForward,
  StopCircle,
  ExternalLink,
  Check,
  GripVertical,
  Clock,
  Save,
  Copy,
  Shuffle,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import {
  applyOrderMode,
  computeSlots,
  formatSlotTime,
  buildShareableText,
  ORDER_MODE_LABELS,
  type OrderMode,
  type EventSchedule,
  type SchedulableTeam,
} from '../lib/schedule';

interface Team {
  teamId: string;
  name: string;
  themeName?: string;
  themeId?: string;
  submittedAt?: string | null;
}

interface EventDetail {
  eventId: string;
  name: string;
  status: string;
  themes?: Array<{ id: string; name: string }>;
  schedule?: EventSchedule;
  settings?: { timerDurationSeconds?: number };
}

// Round a Date to the next N-minute boundary (useful default for "planned start").
function roundToNextBoundary(date: Date, minutes: number): Date {
  const ms = minutes * 60 * 1000;
  return new Date(Math.ceil(date.getTime() / ms) * ms);
}

// Convert ISO -> value suitable for <input type="datetime-local"> (no timezone, minute precision).
function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// datetime-local value -> ISO string (treats value as local time).
function fromDatetimeLocalValue(v: string): string {
  // new Date("2026-04-24T15:30") parses in local tz on all browsers.
  return new Date(v).toISOString();
}

interface LiveSession {
  sessionId: string;
  status: 'active' | 'paused' | 'ended';
  currentTeamId: string | null;
  currentTeamIndex: number;
  teamOrder: Array<{
    teamId: string;
    teamName: string;
    themeName?: string;
    status: 'waiting' | 'presenting' | 'completed';
  }>;
  timerState: 'running' | 'paused' | 'stopped' | 'expired';
  timerRemainingSeconds: number;
  timerDurationSeconds: number;
}

function formatTime(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function timerColorClass(seconds: number, timerState: string): string {
  if (timerState === 'expired' || seconds <= 0) return 'text-red-500';
  if (seconds <= 30) return 'text-yellow-500';
  return 'text-green-500';
}

export function LiveControlPage() {
  const { eventId } = useParams();
  
  const [session, setSession] = useState<LiveSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [noSession, setNoSession] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamOrder, setTeamOrder] = useState<string[]>([]);
  const [timerDuration, setTimerDuration] = useState(300);
  const [starting, setStarting] = useState(false);
  const [eventDetail, setEventDetail] = useState<EventDetail | null>(null);
  const [orderMode, setOrderMode] = useState<OrderMode>('custom');
  const [plannedStartAt, setPlannedStartAt] = useState<string>(() =>
    roundToNextBoundary(new Date(Date.now() + 5 * 60 * 1000), 5).toISOString(),
  );
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [copied, setCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [localRemaining, setLocalRemaining] = useState(0);
  const [scoreCounts, setScoreCounts] = useState<Record<string, number>>({});
  const [scoreJudges, setScoreJudges] = useState<Record<string, string[]>>({});
  const [totalJudges, setTotalJudges] = useState(0);
  const [judgeNames, setJudgeNames] = useState<Record<string, string>>({});
  const [allJudgeIds, setAllJudgeIds] = useState<string[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch live session
  const fetchSession = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await networkManager.get<{ status: boolean; data: LiveSession }>(
        `/api/events/${eventId}/live`,
      );
      setSession(res.data);
      setLocalRemaining(res.data.timerRemainingSeconds);
      setNoSession(false);
    } catch (err: unknown) {
      const error = err as { response?: { status?: number } };
      if (error.response?.status === 404) {
        setNoSession(true);
        setSession(null);
      }
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Initial load
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Load teams + event (for schedule, name, themes) when there's no active session.
  // If an event.schedule exists, hydrate state from it so organizers resume where they left off.
  useEffect(() => {
    if (!eventId || !noSession) return;
    Promise.all([
      networkManager.get<{ status: boolean; data: Team[] }>(`/api/events/${eventId}/teams`),
      networkManager.get<{ status: boolean; data: EventDetail }>(`/api/events/${eventId}`),
    ])
      .then(([teamsRes, eventRes]) => {
        const fetchedEvent = eventRes.data;
        // The team-list API returns themeId but not themeName — resolve it
        // here so render code and the FIFO/theme sort helper (which keys on
        // themeName) have a single source of truth.
        const themeNameById = new Map(
          (fetchedEvent.themes ?? []).map((t) => [t.id, t.name]),
        );
        const fetchedTeams = teamsRes.data.map((t) => ({
          ...t,
          themeName: t.themeId ? themeNameById.get(t.themeId) : undefined,
        }));
        setTeams(fetchedTeams);
        setEventDetail(fetchedEvent);

        const savedSchedule = fetchedEvent.schedule;
        const validIds = new Set(fetchedTeams.map((t) => t.teamId));

        if (savedSchedule) {
          // Filter removed teams, append newly-joined ones at the end.
          const retained = savedSchedule.teamOrder.filter((id) => validIds.has(id));
          const newcomers = fetchedTeams
            .map((t) => t.teamId)
            .filter((id) => !retained.includes(id));
          setTeamOrder([...retained, ...newcomers]);
          setOrderMode(savedSchedule.orderMode);
          setPlannedStartAt(savedSchedule.plannedStartAt);
          setTimerDuration(savedSchedule.timerDurationSeconds);
        } else {
          // No saved schedule — default to creation order, timer from event setting.
          setTeamOrder(fetchedTeams.map((t) => t.teamId));
          if (fetchedEvent.settings?.timerDurationSeconds) {
            setTimerDuration(fetchedEvent.settings.timerDurationSeconds);
          }
        }
      })
      .catch(console.error);
  }, [eventId, noSession]);

  // Fetch scoring progress
  const fetchScoreCounts = useCallback(async () => {
    if (!eventId) return;
    try {
      const [scoresRes, eventRes] = await Promise.all([
        networkManager.get<{ status: boolean; data: Array<{ teamId: string; judgeId: string }> }>(`/api/events/${eventId}/scores`).catch(() => ({ data: [] })),
        networkManager.get<{ status: boolean; data: { judges?: string[] } }>(`/api/events/${eventId}`).catch(() => ({ data: { judges: [] } })),
      ]);
      const counts: Record<string, number> = {};
      const judgesPerTeam: Record<string, string[]> = {};
      for (const s of scoresRes.data) {
        counts[s.teamId] = (counts[s.teamId] ?? 0) + 1;
        if (!judgesPerTeam[s.teamId]) judgesPerTeam[s.teamId] = [];
        judgesPerTeam[s.teamId].push(s.judgeId);
      }
      setScoreCounts(counts);
      setScoreJudges(judgesPerTeam);
      const judges = eventRes.data.judges ?? [];
      setAllJudgeIds(judges);
      setTotalJudges(judges.length);

      // Resolve judge names (only fetch once for new IDs)
      const unknownIds = judges.filter((id) => !judgeNames[id]);
      if (unknownIds.length > 0) {
        networkManager.post<{ status: boolean; data: Array<{ yumId: string; name: string }> }>(
          '/api/users/lookup',
          { yumIds: unknownIds },
        ).then((res) => {
          setJudgeNames((prev) => {
            const next = { ...prev };
            for (const u of res.data) next[u.yumId] = u.name;
            return next;
          });
        }).catch(() => {});
      }
    } catch { /* ignore */ }
  }, [eventId, judgeNames]);

  // Poll when session is active
  useEffect(() => {
    if (!session || session.status === 'ended') return;
    fetchScoreCounts();
    pollRef.current = setInterval(() => { fetchSession(); fetchScoreCounts(); }, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [session?.status, fetchSession, fetchScoreCounts]);

  // Local countdown timer
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!session || session.timerState !== 'running') return;

    timerRef.current = setInterval(() => {
      setLocalRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [session?.timerState, session?.timerRemainingSeconds]);

  // Start session
  async function handleStart() {
    if (!eventId) return;
    setStarting(true);
    try {
      const res = await networkManager.post<{ status: boolean; data: LiveSession }>(
        `/api/events/${eventId}/live`,
        { teamOrder, timerDurationSeconds: timerDuration },
      );
      setSession(res.data);
      setLocalRemaining(res.data.timerRemainingSeconds);
      setNoSession(false);
      setActionError(null);
    } catch (err) {
      setActionError(getErrorMessage(err, 'Failed to start live session'));
    } finally {
      setStarting(false);
    }
  }

  // Control actions
  async function sendAction(action: string) {
    if (!eventId) return;
    setActionLoading(true);
    try {
      if (action === 'end_session') {
        await networkManager.delete<{ status: boolean }>(`/api/events/${eventId}/live`);
        setSession(null);
      } else {
        const res = await networkManager.put<{ status: boolean; data: LiveSession }>(
          `/api/events/${eventId}/live`,
          { action },
        );
        setSession(res.data);
        setLocalRemaining(res.data.timerRemainingSeconds);
      }
      setActionError(null);
    } catch (err) {
      setActionError(getErrorMessage(err, 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  }

  // Any manual reorder flips the mode to 'custom' so subsequent preset changes
  // don't silently overwrite the organizer's hand-tuning.
  function moveTeam(index: number, direction: -1 | 1) {
    const newOrder = [...teamOrder];
    const target = index + direction;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
    setTeamOrder(newOrder);
    setOrderMode('custom');
    setSaveState('idle');
  }

  // Applying a preset always recomputes the full order from the source teams.
  // Re-applying 'shuffle' gives a new draw; re-applying 'custom' is a no-op.
  function applyPreset(nextMode: OrderMode) {
    if (nextMode === 'custom') {
      setOrderMode('custom');
      setSaveState('idle');
      return;
    }
    const schedulable: SchedulableTeam[] = teams.map((t) => ({
      teamId: t.teamId,
      name: t.name,
      themeName: t.themeName ?? null,
      submittedAt: t.submittedAt ?? null,
    }));
    setTeamOrder(applyOrderMode(schedulable, nextMode));
    setOrderMode(nextMode);
    setSaveState('idle');
  }

  // Persist the schedule without starting the session. Teams can see their slot
  // on their dashboard as soon as this succeeds.
  async function handleSaveSchedule() {
    if (!eventId) return;
    setSaveState('saving');
    try {
      const res = await networkManager.put<{ status: boolean; data: EventDetail }>(
        `/api/events/${eventId}/schedule`,
        {
          plannedStartAt,
          timerDurationSeconds: timerDuration,
          teamOrder,
          orderMode,
        },
      );
      setEventDetail(res.data);
      setSaveState('saved');
      setActionError(null);
      setTimeout(() => setSaveState('idle'), 2500);
    } catch (err) {
      setSaveState('error');
      setActionError(getErrorMessage(err, 'Failed to save schedule'));
    }
  }

  // Build a plaintext schedule and drop it on the clipboard. Organizers can
  // paste into Slack/email; we don't need a separate share surface.
  async function handleCopySchedule() {
    if (teamOrder.length === 0) return;
    const teamsById: Record<string, { name: string; themeName?: string | null }> = {};
    for (const t of teams) teamsById[t.teamId] = { name: t.name, themeName: t.themeName ?? null };
    const text = buildShareableText(
      {
        plannedStartAt,
        timerDurationSeconds: timerDuration,
        teamOrder,
        orderMode,
      },
      teamsById,
      eventDetail?.name ?? 'Hackathon',
    );
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError('Could not copy to clipboard. Your browser may have blocked it.');
    }
  }

  // Precompute slots so the preview stays in sync with state.
  const slots = useMemo(
    () => computeSlots(teamOrder, plannedStartAt, timerDuration),
    [teamOrder, plannedStartAt, timerDuration],
  );
  const endTime = useMemo(
    () => (slots.length > 0 ? slots[slots.length - 1].endsAt : null),
    [slots],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
      </div>
    );
  }

  // Pre-session configuration — organizer-facing scheduler + start control
  if (noSession) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const minsPerTeam = Math.floor(timerDuration / 60);
    const secsPerTeam = timerDuration % 60;
    const hasSchedule = !!eventDetail?.schedule;
    const modeHint = ORDER_MODE_LABELS[orderMode].hint;
    // Planning is allowed in any pre-live state; "Start session now" only makes sense
    // once the event has actually transitioned to live_demo. Before that, organizers
    // plan here and use the event page's "Start Live Demo" transition when ready.
    const canStartSession = eventDetail?.status === 'live_demo';
    const isEarlyPlanning = !canStartSession;

    return (
      <div className="animate-fade-in">
        <Link to={`/events/${eventId}`} className="btn-ghost mb-4 inline-flex">
          <ArrowLeft className="h-4 w-4" /> Back to Event
        </Link>

        <header>
          <h1 className="font-display text-3xl font-bold text-ink">
            {isEarlyPlanning ? 'Plan the run of show' : 'Plan the demo day'}
          </h1>
          <p className="mt-1 text-ink-secondary">
            {isEarlyPlanning
              ? 'Lock in the demo order and start time now. Teams will see their slot on their dashboard. You can keep editing until the session begins.'
              : 'Pick an order, set the start time, save the schedule so teams can see their slot — then start the session when you’re ready.'}
          </p>
        </header>

        {/* Schedule basics: start time + per-team duration */}
        <section className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="card p-4">
            <label htmlFor="planned-start" className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Clock className="h-4 w-4 text-ink-tertiary" />
              Session start
            </label>
            <input
              id="planned-start"
              type="datetime-local"
              value={toDatetimeLocalValue(plannedStartAt)}
              onChange={(e) => {
                if (!e.target.value) return;
                setPlannedStartAt(fromDatetimeLocalValue(e.target.value));
                setSaveState('idle');
              }}
              className="mt-2 w-full rounded-lg border border-ring bg-surface-raised px-3 py-2 text-ink"
            />
            <p className="mt-1.5 text-xs text-ink-tertiary">Times shown in your local timezone ({tz}).</p>
          </div>

          <div className="card p-4">
            <label htmlFor="timer-duration" className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Clock className="h-4 w-4 text-ink-tertiary" />
              Per team
            </label>
            <div className="mt-2 flex items-center gap-2">
              <input
                id="timer-duration"
                type="number"
                min={30}
                max={3600}
                step={30}
                value={timerDuration}
                onChange={(e) => {
                  setTimerDuration(Number(e.target.value));
                  setSaveState('idle');
                }}
                className="w-28 rounded-lg border border-ring bg-surface-raised px-3 py-2 text-ink"
              />
              <span className="text-sm text-ink-secondary">seconds</span>
              <span className="ml-auto text-xs text-ink-tertiary">
                = {minsPerTeam}m {secsPerTeam > 0 ? `${secsPerTeam}s` : ''}
              </span>
            </div>
            {endTime && (
              <p className="mt-1.5 text-xs text-ink-tertiary">
                {teamOrder.length} teams · ends around {formatSlotTime(endTime)}
              </p>
            )}
          </div>
        </section>

        {/* Order controls */}
        <section className="mt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-ink">Team order</h2>
              <p className="mt-0.5 text-sm text-ink-secondary">
                Pick a preset or drag to hand-tune. Any manual change switches mode to Custom.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <label htmlFor="order-mode" className="text-xs font-medium uppercase tracking-wider text-ink-tertiary">
                Sort by
              </label>
              <div className="flex gap-2">
                <select
                  id="order-mode"
                  value={orderMode}
                  onChange={(e) => applyPreset(e.target.value as OrderMode)}
                  className="rounded-lg border border-ring bg-surface-raised px-3 py-2 text-sm text-ink"
                >
                  {(Object.keys(ORDER_MODE_LABELS) as OrderMode[]).map((m) => (
                    <option key={m} value={m}>
                      {ORDER_MODE_LABELS[m].label}
                    </option>
                  ))}
                </select>
                {orderMode === 'shuffle' && (
                  <button
                    type="button"
                    className="btn-ghost inline-flex items-center gap-1 px-2"
                    onClick={() => applyPreset('shuffle')}
                    title="Reshuffle"
                  >
                    <Shuffle className="h-4 w-4" /> Re-shuffle
                  </button>
                )}
              </div>
              <p className="max-w-xs text-right text-xs text-ink-tertiary">{modeHint}</p>
            </div>
          </div>

          {/* Team list with slot preview */}
          <ol className="mt-4 space-y-2" aria-label="Team presentation order">
            {teamOrder.length === 0 && (
              <li className="card px-4 py-6 text-center text-sm text-ink-tertiary">
                No teams found for this event.
              </li>
            )}
            {teamOrder.map((teamId, idx) => {
              const team = teams.find((t) => t.teamId === teamId);
              if (!team) return null;
              const slot = slots[idx];
              return (
                <li
                  key={teamId}
                  className="card flex flex-wrap items-center gap-3 px-3 py-3 sm:flex-nowrap sm:px-4"
                >
                  <GripVertical
                    className="hidden h-4 w-4 shrink-0 text-ink-tertiary sm:block"
                    aria-hidden="true"
                  />
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-sm font-semibold text-ink">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{team.name}</p>
                    <p className="truncate text-xs text-ink-tertiary">
                      {team.themeName ?? 'No theme'}
                      {team.submittedAt ? (
                        <> · submitted {new Date(team.submittedAt).toLocaleString()}</>
                      ) : (
                        <> · no submission yet</>
                      )}
                    </p>
                  </div>
                  {slot && (
                    <div className="order-last w-full text-right text-xs sm:order-none sm:w-auto">
                      <p className="font-mono font-medium text-ink">
                        {formatSlotTime(slot.startsAt)}–{formatSlotTime(slot.endsAt)}
                      </p>
                      <p className="text-ink-tertiary">slot {idx + 1}</p>
                    </div>
                  )}
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      className="btn-ghost h-8 w-8 p-0"
                      onClick={() => moveTeam(idx, -1)}
                      disabled={idx === 0}
                      aria-label={`Move ${team.name} up`}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className="btn-ghost h-8 w-8 p-0"
                      onClick={() => moveTeam(idx, 1)}
                      disabled={idx === teamOrder.length - 1}
                      aria-label={`Move ${team.name} down`}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {actionError && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <span>{actionError}</span>
            <button className="text-xs text-red-600 underline" onClick={() => setActionError(null)}>
              Dismiss
            </button>
          </div>
        )}

        {/* Save / share / start — bottom action bar, sticky on mobile */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleSaveSchedule}
              disabled={saveState === 'saving' || teamOrder.length === 0}
            >
              {saveState === 'saving' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saveState === 'saved' ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saveState === 'saved' ? 'Saved' : hasSchedule ? 'Update schedule' : 'Save schedule'}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={handleCopySchedule}
              disabled={teamOrder.length === 0}
            >
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy schedule'}
            </button>
          </div>
          {canStartSession ? (
            <button
              className="btn-primary"
              onClick={handleStart}
              disabled={starting || teamOrder.length === 0}
            >
              {starting && <Loader2 className="h-4 w-4 animate-spin" />}
              <Play className="h-4 w-4" />
              Start session now
            </button>
          ) : (
            <Link
              to={`/events/${eventId}`}
              className="btn-ghost inline-flex items-center gap-1.5 text-sm"
              title="Transition the event to Live Demo from the event page when you're ready"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to event to go live
            </Link>
          )}
        </div>
        {hasSchedule && saveState !== 'saved' && (
          <p className="mt-3 text-xs text-ink-tertiary">
            Teams see the saved schedule on their dashboard. Changes won&apos;t reach them until you
            hit <strong>Update schedule</strong>.
          </p>
        )}
        {isEarlyPlanning && (
          <p className="mt-3 text-xs text-ink-tertiary">
            When you&apos;re ready to go live, head back to the event and hit{' '}
            <strong>Start Live Demo</strong>. Your saved schedule will carry over.
          </p>
        )}
      </div>
    );
  }

  // Active session view
  if (!session) return null;

  const currentTeam = session.teamOrder.find((t) => t.status === 'presenting');
  const completedCount = session.teamOrder.filter((t) => t.status === 'completed').length;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between">
        <Link to={`/events/${eventId}`} className="btn-ghost inline-flex">
          <ArrowLeft className="h-4 w-4" /> Back to Event
        </Link>
        <a
          href={`/events/${eventId}/live/display`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-ghost inline-flex items-center gap-1"
        >
          Open Display <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      <h1 className="mt-4 font-display text-3xl font-bold text-ink">Live Control Panel</h1>

      {/* Current Team + Timer */}
      <div className="mt-6 card p-6">
        {currentTeam ? (
          <>
            <p className="text-sm font-medium uppercase tracking-wider text-ink-tertiary">
              Now Presenting
            </p>
            <h2 className="mt-1 font-display text-2xl font-bold text-ink">
              {currentTeam.teamName}
            </h2>
            {currentTeam.themeName && (
              <p className="mt-1 text-ink-secondary">{currentTeam.themeName}</p>
            )}

            {/* Timer */}
            <div className="mt-6 flex flex-col items-center">
              <p
                className={`font-mono text-6xl font-bold ${timerColorClass(localRemaining, session.timerState)}`}
              >
                {formatTime(localRemaining)}
              </p>

              {/* Progress bar */}
              <div className="mt-4 h-2 w-full max-w-md overflow-hidden rounded-full bg-surface-sunken">
                <div
                  className={`h-full rounded-full transition-all duration-1000 ${
                    localRemaining <= 0
                      ? 'bg-red-500'
                      : localRemaining <= 30
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                  }`}
                  style={{
                    width: `${session.timerDurationSeconds > 0 ? (localRemaining / session.timerDurationSeconds) * 100 : 0}%`,
                  }}
                />
              </div>

              <p className="mt-2 text-sm text-ink-tertiary">
                Team {completedCount + 1} of {session.teamOrder.length}
              </p>
            </div>
          </>
        ) : (
          <p className="text-center text-ink-secondary">
            {session.status === 'ended'
              ? 'Session has ended.'
              : 'No team is currently presenting.'}
          </p>
        )}
      </div>

      {/* Error banner */}
      {actionError && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-center justify-between">
          <span>{actionError}</span>
          <button className="text-xs text-red-600 underline ml-3" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      {/* Controls */}
      <div className="mt-6 flex flex-wrap gap-3">
        {session.timerState === 'stopped' && (
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => sendAction('start_timer')}
            disabled={actionLoading}
          >
            <Play className="h-4 w-4" /> Start Timer
          </button>
        )}
        {session.timerState === 'running' && (
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => sendAction('pause_timer')}
            disabled={actionLoading}
          >
            <Pause className="h-4 w-4" /> Pause
          </button>
        )}
        {session.timerState === 'paused' && (
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => sendAction('resume_timer')}
            disabled={actionLoading}
          >
            <Play className="h-4 w-4" /> Resume
          </button>
        )}
        <button
          className="btn-ghost inline-flex items-center gap-2"
          onClick={() => sendAction('next_team')}
          disabled={actionLoading}
        >
          <SkipForward className="h-4 w-4" /> Next Team
        </button>
        <button
          className="btn-ghost inline-flex items-center gap-2 text-red-600 hover:text-red-700"
          onClick={() => sendAction('end_session')}
          disabled={actionLoading}
        >
          <StopCircle className="h-4 w-4" /> End Session
        </button>
        {actionLoading && <Loader2 className="h-5 w-5 animate-spin text-ink-tertiary" />}
      </div>

      {/* Team Queue */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-ink">Team Queue</h2>
        <div className="mt-3 space-y-2">
          {session.teamOrder.map((team, idx) => (
            <div
              key={team.teamId}
              className={`card flex items-center gap-3 px-4 py-3 ${
                team.status === 'presenting'
                  ? 'border-2 border-purple-500 bg-purple-50'
                  : ''
              }`}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold">
                {team.status === 'completed' ? (
                  <Check className="h-5 w-5 text-green-600" />
                ) : (
                  <span
                    className={
                      team.status === 'presenting'
                        ? 'text-purple-700'
                        : 'text-ink-tertiary'
                    }
                  >
                    {idx + 1}
                  </span>
                )}
              </span>
              <div className="flex-1">
                <p
                  className={`font-medium ${
                    team.status === 'presenting' ? 'text-purple-700' : 'text-ink'
                  }`}
                >
                  {team.teamName}
                </p>
                {team.themeName && (
                  <p className="text-sm text-ink-secondary">{team.themeName}</p>
                )}
              </div>
              {totalJudges > 0 && (
                <div className="flex flex-col items-end gap-0.5">
                  <span className={`text-xs font-medium ${
                    (scoreCounts[team.teamId] ?? 0) >= totalJudges ? 'text-green-600' : 'text-ink-tertiary'
                  }`}>
                    {scoreCounts[team.teamId] ?? 0}/{totalJudges} scored
                  </span>
                  <div className="flex gap-1">
                    {allJudgeIds.map((jid) => {
                      const scored = (scoreJudges[team.teamId] ?? []).includes(jid);
                      return (
                        <span
                          key={jid}
                          title={`${judgeNames[jid] ?? jid}: ${scored ? 'Scored' : 'Pending'}`}
                          className={`inline-block h-2 w-2 rounded-full ${scored ? 'bg-green-500' : 'bg-gray-300'}`}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  team.status === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : team.status === 'presenting'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-gray-100 text-gray-600'
                }`}
              >
                {team.status}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Judge Scoring Progress */}
      {totalJudges > 0 && (
        <section className="mt-8">
          <h2 className="text-lg font-semibold text-ink">Judge Progress</h2>
          <div className="mt-3 card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ring bg-surface-sunken">
                  <th className="px-4 py-2 text-left font-medium text-ink-secondary">Judge</th>
                  {session.teamOrder.map((t) => (
                    <th key={t.teamId} className="px-2 py-2 text-center font-medium text-ink-secondary text-xs">{t.teamName}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allJudgeIds.map((jid) => (
                  <tr key={jid} className="border-b border-ring last:border-0">
                    <td className="px-4 py-2 font-medium text-ink">{judgeNames[jid] ?? jid}</td>
                    {session.teamOrder.map((t) => {
                      const scored = (scoreJudges[t.teamId] ?? []).includes(jid);
                      return (
                        <td key={t.teamId} className="px-2 py-2 text-center">
                          {scored ? (
                            <Check className="mx-auto h-4 w-4 text-green-600" />
                          ) : (
                            <span className="inline-block h-3 w-3 rounded-full bg-gray-200" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
